import { Platform } from 'react-native';
import { auth, functions, httpsCallable, signInWithCustomToken } from '../config/firebase';
import { createInstallToken, verifyInstallToken } from './installTokenService';
import { ServiceResponse } from './authService';

export interface InstallAuthResult {
  success: boolean;
  userId?: string;
  error?: string;
}

export const generateInstallTokenForWeb = async (): Promise<ServiceResponse<string>> => {
  if (Platform.OS !== 'web') {
    return { success: false, error: 'This function should only be called from web' };
  }

  if (!auth.currentUser) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const result = await createInstallToken();
    if (!result.success || !result.data) {
      return { success: false, error: result.error || 'Failed to generate token' };
    }
    return { success: true, data: result.data };
  } catch (error: any) {
    console.error('[AppsFlyerAuth] Error generating token:', error);
    return { success: false, error: error.message || 'Failed to generate install token' };
  }
};

const exchangeTokenForCustomToken = async (installToken: string): Promise<ServiceResponse<string>> => {
  try {
    if (!installToken || !functions) {
      return { success: false, error: 'No token provided' };
    }

    const exchangeFunction = httpsCallable(functions, 'exchangeInstallToken');
    const result = await exchangeFunction({ installToken });
    const data = result.data as any;

    if (!data.customToken) {
      return { success: false, error: data.error || 'No custom token returned' };
    }

    return { success: true, data: data.customToken };
  } catch (error: any) {
    let errorMessage = 'Failed to exchange token';
    
    if (error.code === 'functions/not-found') {
      errorMessage = 'Exchange function not deployed';
    } else if (error.code === 'functions/invalid-argument') {
      errorMessage = 'Invalid token';
    } else if (error.code === 'functions/deadline-exceeded') {
      errorMessage = 'Token expired';
    } else if (error.code === 'functions/failed-precondition') {
      errorMessage = 'Token already used';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return { success: false, error: errorMessage };
  }
};

export const authenticateWithInstallToken = async (
  installToken: string
): Promise<InstallAuthResult> => {
  if (Platform.OS === 'web' || !installToken || installToken.length < 32) {
    return { success: false, error: 'Invalid token' };
  }

  try {
    const exchangeResult = await exchangeTokenForCustomToken(installToken);
    if (!exchangeResult.success || !exchangeResult.data) {
      return { success: false, error: exchangeResult.error };
    }

    const userCredential = await signInWithCustomToken(auth, exchangeResult.data);
    if (!userCredential?.user) {
      return { success: false, error: 'Sign in failed' };
    }

    console.log('[AppsFlyerAuth] Successfully authenticated user:', userCredential.user.uid);
    return { success: true, userId: userCredential.user.uid };
  } catch (error: any) {
    return { success: false, error: error.message || 'Authentication failed' };
  }
};

